import React, { useContext, useEffect } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import { StyledGroup } from './style'
import { ClimaOptionsContext } from '.'
import CO2Service from '~/api/services/co2'
import { useParams } from 'react-router-dom'
import { handleErrors } from '~/utils/helpers'
import InfoPopup from '~/components/InfoPopup'

const tableColumnHeaders = [
  {
    name: 'Arbeitsgänge',
    key: 'label',
    info: 'Bitte geben Sie hier die Anzahl der im Jahresverlauf ausgeführten Arbeitsgänge an. Bei vier Schnitten ist im Feld “Mähen” entsprechend eine 4 einzutragen.'
  },
  {
    name: 'Anzahl pro Jahr',
    key: 'passages',
    type: 'input',
    unit: 'Durchgänge'
  }
]

const ThirdGrassStep: React.FC = () => {
  const { control, clearErrors, getValues, formState: { errors } } = useFormContext()
  const { workOperations, setIsLoadingResults, setWorkOperations } = useContext(ClimaOptionsContext)
  const { id } = useParams()

  const { fields, replace, update } = useFieldArray({
    control,
    name: 'grassSilage.workOperations',
    keyName: 'key'
  })

  useEffect(() => {
    const idMain = getValues('grassSilage.harvestingMethods[0].id')
    if (idMain) {
      fetchWorkOperations()
    }
  }, [])

  async function fetchWorkOperations() {
    setIsLoadingResults(true)
    try {
      const grassSilageValues = getValues('grassSilage')
      const payload = {
        harvestingMethodId: grassSilageValues?.harvestingMethods[0]?.id,
        pruningPhaseId: grassSilageValues?.pruningPhaseId,
        locationFavorability: grassSilageValues?.locationFavorability,
        fertilizationType: grassSilageValues?.fertilizationType,
        cuttingFrequency: grassSilageValues?.cuttingFrequency
      }
      const response = await CO2Service.getGrassSilagePredefinedData(id, payload)
      const workOperationsData = response.data.workOperations.map(workOperation => {
        const values = fields.find(field => field['id'] === workOperation.id)
        return {
          ...workOperation,
          passages: values ? values['passages'] : workOperation.defaultValue
        }
      })
      replace(workOperationsData)
      setWorkOperations(response.data.workOperations)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsLoadingResults(false)
    }
  }

  function renderInputCellValue(name: string, unit: string, operationIndex: number) {
    return <FormFields.Input
      placeholder={'Anzahl pro Jahr'}
      name={name}
      key={name}
      suffix={' mal pro Jahr'}
      onChange={(event) => {
        update(operationIndex, {
          ...fields[operationIndex],
          passages: event?.target?.value?.replace(unit, '')
        })
        clearErrors(name)
        if (errors?.grassSilage?.['seedUsed']) {
          clearErrors('grassSilage.seedUsed')
        }
        clearErrors('grassSilage.workOperations')
      }}
      rules={{
        validate: (value) => {
          const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
          const limit = workOperations[operationIndex]?.['limitValue'] ? workOperations[operationIndex]?.['limitValue'] : null

          if (limit && transformValue > limit) {
            return 'Die Anzahl pro Jahr darf maximal 10 betragen.'
          }
          return true
        }
      }}
      disabled={workOperations[operationIndex]?.disabled === 1}
      control={control}
      typeInput={'controlled-inline-error'}
      withoutAsterisk
      noErrorMessage
      allowDecimals
      decimalScale={1}
    />
  }

  function renderCell(workOperation, name, column, operationIndex: number) {
    switch (column.type) {
      case 'input':
        return renderInputCellValue(name, column?.unit, operationIndex)
      default:
        return workOperation[column.key]
    }
  }

  function renderTableBody() {
    return workOperations?.map((field, index) => {
      return <Table.Row key={field.id}>
        {tableColumnHeaders.map(column => {
          const name = `grassSilage.workOperations[${index}].${column.key}`
          if (column.key === 'label') {
            return <Table.Cell key={`${column.key}-${field['id']}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <label>{field.label}</label>
                {field.info && <InfoPopup
                  content={field.info}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />}
              </div>
            </Table.Cell>
          }
          return <Table.Cell key={`${column.key}-${field['id']}`}>
            {renderCell(field, name, column, index)}
          </Table.Cell>
        })}
      </Table.Row>
    })
  }

  const workOperationsWithValues = fields.filter(workOp => workOp['passages'])

  function renderTable() {
    return <Table celled striped compact>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(header => {
            return <Table.HeaderCell key={header.key}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{header.name}</div>
                {header.info && <InfoPopup
                  content={header.info}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em', color: 'white' }}
                  position={'top center'}
                />}
              </div>
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  return (
    <SilageStepLayout
      infoText={`Tragen Sie die durchschnittliche Anzahl der jeweiligen Arbeitsgänge pro Jahr ein. 
        Die Abfrage bezieht sich auf eine durchschnittliche Grünlandfläche Ihres Betriebs, 
        unabhängig der absoluten Anzahl an Wiesen. Beispiel: Sie wenden beim ersten Schnitt 2x und beim zweiten und dritten Schnitt 1x. Somit haben Sie 4x pro Jahr gewendet.`}
    >
      <Controller
        control={control}
        name='grassSilage.workOperations'
        rules={{
          validate: (operations) => {
            if (!operations || operations.length === 0) {
              return 'Work operations are required'
            }

            return true
          }
        }}
        render={({ fieldState: { error } }) => (
          <>
            {fields.length === workOperations.length && renderTable()}
          </>
        )}
      />
      <StyledGroup widths={'equal'}>
        <FormFields.Input
          control={control}
          rules={{
            required: Boolean(workOperationsWithValues.find(workOp => workOp['seedsRequired'] === 1))
          }}
          withoutAsterisk
          noErrorMessage
          suffix=' kg/ha'
          typeInput={'controled'}
          allowDecimals
          decimalScale={2}
          label={'Wie viel Saatgut wurde eingesetzt?'}
          placeholder={'in kg/ha'}
          name={'grassSilage.seedUsed'}
        />
      </StyledGroup>
    </SilageStepLayout>
  )
}

export default ThirdGrassStep